import type { EligibilityRule } from '@kit/shared/eligibility';

export interface ClassEligibility {
  fieldId: string;
  label: string;
  rule: EligibilityRule;
}

/** The age filter's "Adults" tile; it matches any class open to some adult age. */
export const ADULT_AGE = 18;

export const TIMES_OF_DAY = ['morning', 'afternoon', 'evening'] as const;
export type TimeOfDay = (typeof TIMES_OF_DAY)[number];

export type FieldFilterKind = 'age' | 'number' | 'select' | 'boolean';
/** Age filters hold the picked ages; a class matches when it accepts any of them. */
export type FieldFilterValue = string | number | boolean | number[];

export interface FieldFilter {
  fieldId: string;
  label: string;
  kind: FieldFilterKind;
  options: string[];
}

/** What a filter needs from a class: its weekly slots and eligibility rules. */
export interface FilterableClass {
  /** JavaScript weekday (Sunday = 0) and HH:MM:SS start time. */
  slots: Array<{ weekday: number; startTime: string }>;
  eligibility: ClassEligibility[];
}

export interface ClassFilterState {
  weekday: number | null;
  timeOfDay: TimeOfDay | null;
  /** Applied field filters keep their field so the chip survives a list change. */
  fields: Record<string, { field: FieldFilter; value: FieldFilterValue }>;
}

export interface ClassFilterOptions {
  weekdays: number[];
  timesOfDay: TimeOfDay[];
  fields: FieldFilter[];
}

export const EMPTY_CLASS_FILTERS: ClassFilterState = {
  weekday: null,
  timeOfDay: null,
  fields: {},
};

/** Morning before 12:00, afternoon 12:00–16:59, evening from 17:00. */
export function getTimeOfDay(startTime: string): TimeOfDay {
  const hour = Number(startTime.slice(0, 2));
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  return 'evening';
}

/** JavaScript weekday of a YYYY-MM-DD date, independent of the device timezone. */
export function getDateWeekday(date: string) {
  return new Date(`${date}T12:00:00Z`).getUTCDay();
}

function getFilterKind(rule: EligibilityRule): FieldFilterKind {
  switch (rule.operator) {
    case 'age_range':
      return 'age';
    case 'range':
      return 'number';
    case 'in':
      return 'select';
    case 'equals':
      return 'boolean';
  }
}

function addFieldOptions(
  fields: Map<string, FieldFilter>,
  field: FieldFilter,
  options: string[],
) {
  const existing = fields.get(field.fieldId) ?? { ...field, options: [] };
  existing.options = [...new Set([...existing.options, ...options])];
  fields.set(field.fieldId, existing);
}

/**
 * Filters worth offering for the listed classes: days and times that occur,
 * fields some class restricts with only the values a class accepts, plus every
 * applied filter so a value never hides classes without a visible chip.
 */
export function getClassFilterOptions(
  classes: FilterableClass[],
  applied: ClassFilterState = EMPTY_CLASS_FILTERS,
): ClassFilterOptions {
  const weekdays = new Set<number>();
  const timesOfDay = new Set<TimeOfDay>();
  const fields = new Map<string, FieldFilter>();

  for (const entry of classes) {
    for (const slot of entry.slots) {
      weekdays.add(slot.weekday);
      timesOfDay.add(getTimeOfDay(slot.startTime));
    }

    for (const { fieldId, label, rule } of entry.eligibility) {
      addFieldOptions(
        fields,
        { fieldId, label, kind: getFilterKind(rule), options: [] },
        rule.operator === 'in' ? rule.values : [],
      );
    }
  }

  if (applied.weekday !== null) weekdays.add(applied.weekday);
  if (applied.timeOfDay !== null) timesOfDay.add(applied.timeOfDay);
  for (const { field, value } of Object.values(applied.fields)) {
    addFieldOptions(
      fields,
      field,
      field.kind === 'select' ? [String(value)] : [],
    );
  }

  return {
    weekdays: [...weekdays].sort((left, right) => left - right),
    timesOfDay: TIMES_OF_DAY.filter((time) => timesOfDay.has(time)),
    fields: [...fields.values()],
  };
}

function ruleAllows(rule: EligibilityRule, value: FieldFilterValue): boolean {
  if (Array.isArray(value))
    return value.some((entry) => ruleAllows(rule, entry));

  switch (rule.operator) {
    case 'age_range':
      if (typeof value === 'number' && value >= ADULT_AGE) {
        return rule.max === undefined || rule.max >= ADULT_AGE;
      }
      return (
        typeof value === 'number' &&
        (rule.min === undefined || value >= rule.min) &&
        (rule.max === undefined || value <= rule.max)
      );
    case 'range':
      return (
        typeof value === 'number' &&
        (rule.min === undefined || value >= rule.min) &&
        (rule.max === undefined || value <= rule.max)
      );
    case 'in':
      return typeof value === 'string' && rule.values.includes(value);
    case 'equals':
      return rule.value === value;
  }
}

/**
 * A class matches when one of its weekly slots fits the day/time filters and
 * every rule on a filtered field allows the value. Classes without a rule on a
 * field are open to everyone, so they always pass that field's filter.
 */
export function matchesClassFilters(
  entry: FilterableClass,
  filters: ClassFilterState,
) {
  const hasTimeFilter = filters.weekday !== null || filters.timeOfDay !== null;
  const timeMatches =
    !hasTimeFilter ||
    entry.slots.some(
      (slot) =>
        (filters.weekday === null || slot.weekday === filters.weekday) &&
        (filters.timeOfDay === null ||
          getTimeOfDay(slot.startTime) === filters.timeOfDay),
    );

  return (
    timeMatches &&
    Object.entries(filters.fields).every(([fieldId, { value }]) =>
      entry.eligibility
        .filter((rule) => rule.fieldId === fieldId)
        .every((rule) => ruleAllows(rule.rule, value)),
    )
  );
}

export function countActiveClassFilters(filters: ClassFilterState) {
  return (
    Number(filters.weekday !== null) +
    Number(filters.timeOfDay !== null) +
    Object.keys(filters.fields).length
  );
}
